import React, { useMemo, useState } from "react";
import {
  CheckCircle2,
  AlertTriangle,
  Search,
  Filter,
  CheckCheck,
  Check,
  Cpu,
  FileCheck2,
  ShieldAlert,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import { approveAll, approveTransaction, reclassifyTransaction, fetchTransactions } from "../../api";

const fmtAmount = (amount) =>
  `${amount < 0 ? "-" : ""}$${Math.abs(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
  })}`;

const WorkspaceView = ({ transactions, summary, coaOptions, targetForms, defaultForm, onDataUpdate, onSwitchView }) => {
  const [selectedForm, setSelectedForm] = useState(defaultForm || "1065");
  const [activeId, setActiveId] = useState(transactions[0] ? transactions[0].id : null);
  const [filterText, setFilterText] = useState("");
  const [exceptionsOnly, setExceptionsOnly] = useState(false);
  const [checkedIds, setCheckedIds] = useState([]);

  const activeTx = transactions.find((t) => t.id === activeId) || transactions[0];

  const pendingHighConfidence = useMemo(
    () => transactions.filter((t) => !t.eliminated && t.needs_review && t.confidence >= 90).length,
    [transactions]
  );

  const visibleTransactions = useMemo(() => {
    let list = transactions;
    if (exceptionsOnly) list = list.filter((t) => !t.eliminated && t.needs_review);
    if (filterText.trim()) {
      const q = filterText.toLowerCase();
      list = list.filter(
        (t) =>
          t.payee.toLowerCase().includes(q) ||
          (t.tax_lines[selectedForm] || "").toLowerCase().includes(q) ||
          String(Math.abs(t.amount)).includes(q)
      );
    }
    return list;
  }, [transactions, exceptionsOnly, filterText, selectedForm]);

  const refreshFromServer = async () => {
    const d = await fetchTransactions();
    onDataUpdate({ transactions: d.transactions, summary: d.summary });
  };

  const toggleCheck = (id) => {
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const handleApproveAll = async () => {
    try {
      const res = await approveAll();
      await refreshFromServer();
      toast.success(`${res.approved} high-confidence items approved`, {
        description: "All items \u2265 90% confidence locked to IRS schedule lines.",
      });
    } catch {
      toast.error("Batch approval failed");
    }
  };

  const handleApproveLine = async (e, id) => {
    e.stopPropagation();
    try {
      const res = await approveTransaction(id);
      onDataUpdate({
        transactions: transactions.map((t) => (t.id === id ? res.transaction : t)),
        summary: res.summary,
      });
      toast.success("Line item approved", { description: "Mapping locked; event on the audit chain." });
    } catch {
      toast.error("Approval failed");
    }
  };

  const handleReclassify = async (id, coaCode) => {
    try {
      const res = await reclassifyTransaction(id, coaCode, false);
      onDataUpdate({
        transactions: transactions.map((t) => (t.id === id ? res.transaction : t)),
        summary: res.summary,
      });
      toast.success("Reclassified", {
        description: `Mapped to ${res.transaction.coa_account} — ${res.transaction.coa_name}.`,
      });
    } catch {
      toast.error("Reclassification failed");
    }
  };

  const handleConfirmTrain = async () => {
    if (!activeTx || !activeTx.coa_account) return;
    try {
      const res = await reclassifyTransaction(activeTx.id, activeTx.coa_account, true);
      onDataUpdate({
        transactions: transactions.map((t) => (t.id === activeTx.id ? res.transaction : t)),
        summary: res.summary,
      });
      toast.success("Category confirmed & entity rule promoted", {
        description: res.rule
          ? `Rule ${res.rule.rule_id}: "${res.rule.payee}" \u2192 ${res.rule.account} learned for this entity.`
          : "Feedback saved.",
      });
    } catch {
      toast.error("Training failed");
    }
  };

  if (!activeTx) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-zinc-400" data-testid="view-workspace">
        No transactions yet — upload a statement to begin.
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="view-workspace">
      {/* RECONCILIATION SUMMARY BAR */}
      <section className="glass-panel border-b border-white/10 px-6 py-2.5 flex items-center justify-between text-xs flex-shrink-0">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <span className="text-zinc-400">Statement Health:</span>
            {summary.reconciled ? (
              <span data-testid="statement-health-badge" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Reconciled ({fmtAmount(summary.reconciled_amount)})
              </span>
            ) : (
              <button
                data-testid="statement-health-badge"
                onClick={() => onSwitchView("signoff")}
                title="Open Sign-Off view to review and waive blocking exceptions"
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-200 transition cursor-pointer"
              >
                <AlertTriangle className="w-3 h-3 mr-1" /> {summary.open_blocking} Blocking Exception{summary.open_blocking === 1 ? "" : "s"} — Review &amp; Waive →
              </button>
            )}
          </div>
          <div className="h-4 w-px bg-white/10"></div>
          <div>
            <span className="text-zinc-400">Total Items:</span>
            <span className="font-bold text-white ml-1" data-testid="summary-total-items">{summary.total_items}</span>
          </div>
          <div className="h-4 w-px bg-white/10"></div>
          <div>
            <span className="text-zinc-400">Auto-Mapped:</span>
            <span className="font-bold text-emerald-400 ml-1" data-testid="summary-auto-mapped">
              {summary.auto_mapped} ({summary.auto_mapped_pct}%)
            </span>
          </div>
          <div className="h-4 w-px bg-white/10"></div>
          <div>
            <span className="text-zinc-400">Needs Review:</span>
            <span className="font-bold text-amber-400 ml-1" data-testid="summary-needs-review">{summary.needs_review}</span>
          </div>
          <div className="h-4 w-px bg-white/10"></div>
          <div>
            <span className="text-zinc-400">Eliminated:</span>
            <span className="font-bold text-purple-400 ml-1" data-testid="summary-eliminated">{summary.eliminated_items}</span>
          </div>
        </div>

        <div className="hidden sm:flex items-center space-x-3">
          <div className="flex items-center space-x-2 bg-zinc-900 border border-white/10 px-3 py-1 rounded-md">
            <span className="text-zinc-400 text-[11px]">IRS Target:</span>
            <select
              data-testid="target-form-select"
              value={selectedForm}
              onChange={(e) => setSelectedForm(e.target.value)}
              className="bg-transparent text-xs font-bold text-blue-400 focus:outline-none cursor-pointer"
            >
              {targetForms.map((f) => (
                <option key={f.value} value={f.value} className="bg-zinc-900">
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* SPLIT WORKSPACE GRID */}
      <div className="flex-1 flex overflow-hidden">
        <main className="flex-1 flex flex-col min-w-0 bg-white/[0.04]">
          {/* TOOLBAR */}
          <div className="p-4 bg-zinc-950/70 border-b border-white/10 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-2.5" />
                <input
                  type="text"
                  data-testid="filter-input"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder="Filter payee, tax line, amount..."
                  className="pl-8 pr-4 py-1.5 text-xs bg-zinc-900 border border-white/20 rounded-md w-64 text-zinc-300 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                data-testid="exceptions-only-btn"
                onClick={() => setExceptionsOnly((p) => !p)}
                className={`px-3 py-1.5 border text-xs font-medium rounded-md transition flex items-center space-x-1 ${
                  exceptionsOnly
                    ? "bg-amber-500/10 border-amber-500/40 text-amber-400"
                    : "bg-zinc-900 hover:bg-white/5 border-white/10 text-zinc-400"
                }`}
              >
                <Filter className={`w-3.5 h-3.5 ${exceptionsOnly ? "text-amber-400" : "text-zinc-500"}`} />
                <span>Needs Review Only</span>
              </button>
              <button
                data-testid="approve-all-btn"
                onClick={handleApproveAll}
                disabled={pendingHighConfidence === 0}
                className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-md transition shadow-glow-emerald flex items-center space-x-1"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                <span>Approve {pendingHighConfidence} High-Confidence Items</span>
              </button>
            </div>
          </div>

          {/* TRANSACTION TABLE */}
          <div className="flex-1 overflow-auto custom-scrollbar">
            <table className="w-full text-left border-collapse text-xs">
              <thead className="bg-zinc-900/95 sticky top-0 z-10 border-b border-white/10 text-zinc-400 font-semibold uppercase tracking-wider backdrop-blur-md">
                <tr>
                  <th className="py-3 px-4 w-8"></th>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Payee / Description</th>
                  <th className="py-3 px-4 text-right">Amount</th>
                  <th className="py-3 px-4">IRS Form Schedule Line</th>
                  <th className="py-3 px-4 text-center">Class / Type</th>
                  <th className="py-3 px-4 text-center">Confidence</th>
                  <th className="py-3 px-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 text-zinc-300 bg-zinc-900" data-testid="transaction-table-body">
                {visibleTransactions.map((tx) => {
                  const isSelected = tx.id === activeId;
                  const isApproved = !tx.needs_review && !tx.eliminated;
                  return (
                    <tr
                      key={tx.id}
                      data-testid={`transaction-row-${tx.id}`}
                      onClick={() => setActiveId(tx.id)}
                      className={`hover:bg-blue-500/10 cursor-pointer transition ${
                        isSelected ? "bg-blue-500/10 border-l-2 border-blue-500" : ""
                      } ${tx.eliminated ? "opacity-50" : ""}`}
                    >
                      <td className="py-3 px-4 text-center">
                        <input
                          type="checkbox"
                          checked={checkedIds.includes(tx.id)}
                          onChange={() => toggleCheck(tx.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border-white/20 bg-zinc-900 text-blue-400 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap text-zinc-400 font-mono text-[11px]">{tx.date}</td>
                      <td className="py-3 px-4 font-semibold text-zinc-200">
                        {tx.payee}
                        <span className="block text-[10px] font-normal text-zinc-500">{tx.account}</span>
                      </td>
                      <td className={`py-3 px-4 text-right font-mono font-semibold ${tx.amount < 0 ? "text-zinc-200" : "text-emerald-400"}`}>
                        {fmtAmount(tx.amount)}
                      </td>
                      <td className="py-3 px-4">
                        {tx.eliminated ? (
                          <span className="text-[11px] text-purple-400 font-semibold">{tx.tax_lines[selectedForm]}</span>
                        ) : (
                          <select
                            data-testid={`schedule-line-select-${tx.id}`}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => handleReclassify(tx.id, e.target.value)}
                            className="bg-zinc-900 border border-white/20 rounded-md px-2.5 py-1 text-xs text-zinc-300 focus:ring-1 focus:ring-blue-500 focus:outline-none w-full cursor-pointer"
                            value={tx.coa_account || ""}
                          >
                            <option value={tx.coa_account || ""}>
                              {tx.tax_lines[selectedForm]} · {tx.coa_name}
                            </option>
                            {coaOptions
                              .filter((o) => o.code !== tx.coa_account)
                              .map((opt) => (
                                <option key={opt.code} value={opt.code}>
                                  {opt.label}
                                </option>
                              ))}
                          </select>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-block px-2.5 py-0.5 border rounded-full text-[10px] font-bold ${tx.typeClass}`}>
                          {tx.type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span
                          className={`inline-flex items-center font-bold font-mono text-[11px] ${
                            tx.confidence >= 90 ? "text-emerald-400" : "text-amber-400"
                          }`}
                        >
                          {tx.confidence}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {tx.eliminated ? (
                          <span className="text-[10px] text-purple-500 font-semibold uppercase">Eliminated</span>
                        ) : (
                          <button
                            title="Approve Line"
                            data-testid={`approve-line-${tx.id}`}
                            onClick={(e) => handleApproveLine(e, tx.id)}
                            className={`p-1 hover:bg-white/5 rounded transition ${
                              isApproved ? "text-emerald-400" : "text-zinc-500 hover:text-emerald-400"
                            }`}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <footer className="px-6 py-2 bg-zinc-950/80 border-t border-white/10 flex items-center justify-between text-xs text-zinc-400 flex-shrink-0">
            <div className="flex items-center space-x-2">
              <Cpu className="w-3.5 h-3.5 text-blue-400" />
              <span>Deterministic In-Boundary Classification Engine Active</span>
            </div>
            <div data-testid="table-footer-count">
              Showing {visibleTransactions.length} of {summary.total_items} items
            </div>
          </footer>
        </main>

        {/* RIGHT PANEL: OCR PROOF & TAX LOGIC */}
        <aside className="w-96 border-l border-white/10 glass-panel hidden lg:flex flex-col flex-shrink-0" data-testid="ocr-panel">
          <div className="p-3.5 border-b border-white/10 flex items-center justify-between bg-zinc-950/70">
            <div className="flex items-center space-x-2">
              <FileCheck2 className="w-4 h-4 text-blue-400" />
              <span className="text-xs font-bold tracking-wider uppercase text-zinc-300">Statement Line Proof</span>
            </div>
            <span className="text-[10px] bg-white/5 border border-white/10 px-2 py-0.5 rounded text-zinc-400 font-mono">{activeTx.account}</span>
          </div>

          <div className="p-4 border-b border-white/10 bg-white/[0.03]">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400 mb-2">RAW EXTRACTED LINE ITEM:</div>
            <div className="bg-amber-500/10 border border-amber-500/40 rounded-md p-3.5 text-xs font-mono shadow-sm transition-all" data-testid="pdf-bounding-box">
              <div className="flex justify-between text-zinc-400 mb-1">
                <span>{activeTx.date}</span>
                <span className={`font-bold ${activeTx.amount < 0 ? "text-amber-400" : "text-emerald-400"}`}>
                  {fmtAmount(activeTx.amount)}
                </span>
              </div>
              <div className="text-white font-semibold">{activeTx.payee}</div>
              <div className="text-[10px] text-zinc-400 mt-2 flex items-center justify-between pt-2 border-t border-amber-500/30">
                <span>{activeTx.pageLine}</span>
                <span className="text-emerald-400 flex items-center">
                  <Check className="w-3 h-3 mr-1" /> Tie-Out Verified (C-02)
                </span>
              </div>
            </div>
          </div>

          <div className="flex-1 p-4 overflow-y-auto custom-scrollbar space-y-4">
            <div>
              <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Classification Logic</h3>
              <div className="glass-card rounded-md p-3.5 text-xs space-y-2.5">
                <p className="text-zinc-400 leading-relaxed" data-testid="reasoning-text">
                  {activeTx.reasoning}
                </p>
                <div className="pt-2 border-t border-white/10 flex justify-between text-[11px]">
                  <span className="text-zinc-400">COA Account:</span>
                  <span className="text-zinc-200 font-bold">
                    {activeTx.coa_account} — {activeTx.coa_name}
                  </span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-zinc-400">Nature:</span>
                  <span className="text-zinc-200 font-bold">{activeTx.nature_label}</span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-zinc-400">MACRS Asset Class:</span>
                  <span className="text-amber-400 font-bold">{activeTx.macrs}</span>
                </div>
                {activeTx.macrs !== "N/A" && (
                  <button
                    data-testid="view-macrs-btn"
                    onClick={() => onSwitchView("reports:depreciation")}
                    className="w-full mt-1 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-200 border border-amber-500/40 text-amber-800 text-[11px] font-semibold rounded-md transition"
                  >
                    View Form 4562 Depreciation Schedule →
                  </button>
                )}
              </div>
            </div>

            {activeTx.control_flags.length > 0 && (
              <div className="bg-purple-50 border border-purple-500/30 rounded-md p-3.5 text-xs space-y-2" data-testid="control-flags-box">
                <div className="flex items-center space-x-2 text-purple-300 font-semibold">
                  <ShieldAlert className="w-4 h-4 text-purple-400" />
                  <span>Integrity Control Flags</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {activeTx.control_flags.map((f) => (
                    <span key={f} className="px-2 py-0.5 bg-zinc-900 border border-purple-500/30 rounded text-[10px] font-mono text-purple-300">
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-md p-3.5 text-xs space-y-2">
              <div className="flex items-center space-x-2 text-blue-300 font-semibold">
                <ShieldAlert className="w-4 h-4 text-blue-400" />
                <span>Tax Line Mapping — All Forms</span>
              </div>
              <div className="space-y-1 text-[11px]">
                {Object.entries(activeTx.tax_lines).map(([form, line]) => (
                  <div key={form} className="flex justify-between">
                    <span className="text-zinc-400">Form {form}:</span>
                    <span className={`font-semibold ${form === selectedForm ? "text-blue-300" : "text-zinc-300"}`}>{line}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-white/10 bg-zinc-950/70 flex-shrink-0">
            <button
              data-testid="confirm-train-btn"
              onClick={handleConfirmTrain}
              disabled={activeTx.eliminated}
              className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-md text-xs transition shadow-glow-blue flex items-center justify-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>Confirm &amp; Promote Entity Rule</span>
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default WorkspaceView;
